import _ from "lodash"
// import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"

import propertyService from "data/property"
import Client from "data/_client"
import { ApiResult } from "data/interface/api"
import notificationService from "data/notification"
import { PromiseWatch } from "data/lib/promise-helpers"
import { push_undo } from 'data/lib/undo'
import { diff_obj } from 'data/lib/diff'
import { handleReferences } from 'data/lib/handle-references'
import { local } from "data/lib/local-json"
import folderPermissionService from "./folder-permission"
import calcService from "data/calc"
const { fetchCalc } = calcService
// private module logic //

export type RentRoll = {
  rentrollid: number
  parentrentrollid: number
  propertyid: number
  sortorder: number
  systeminput?: string
  suite: string
}
const id: string = "rentroll"
const initialState: ApiResult<RentRoll[]> = { id, state: "init", value: [] }
const rentrollStream = new BehaviorSubject<ApiResult<RentRoll[]>>(initialState)
const rentrollEventStream = new BehaviorSubject<{ type: string; payload: any }>({
  type: '',
  payload: {}
})

let rentrollMap: {[key: number]: RentRoll} = {}
const _fetches_ = PromiseWatch()
const _patches_ = PromiseWatch()

function clear(): void {
  rentrollStream.next({ id, state: 'ok', value: [] })
  rentrollMap = {}
}

function get(rentrollid: number): RentRoll {
  return rentrollMap[rentrollid]
}

function refresh(): Promise<any> {
  const currentValue = rentrollStream.getValue().value
  const propertyid = _.get(currentValue, '0.propertyid')
  if (_.isNil(propertyid)) return Promise.resolve()
  return fetchRentRoll(propertyid)
}

function create(propertyid: number): Promise<RentRoll | { message: string }> {
  const currentValue = rentrollStream.getValue().value
  const property = propertyService.get();
  const defaults = property['defaultsrentroll'];
  rentrollStream.next({ id, state: 'busy', value: currentValue })

  const payload = {
    propertyid,
    sortorder: (_(currentValue).map('sortorder').min() - 1) || 0,
    begindate: {
      date: null,
      dateMode: "absolute"
    },
    breakpointdetail: {
      breakPoint: defaults['breakPoint'],
      state: "basic"
    },
    enddate: {
      date: null,
      dateMode: "absolute"
    },
    notedetail: {
      notes: ""
    },
    renewaltype: defaults['renewalType'],
    status: defaults['status'],
    suite: "",
    tenant: "",
    type: defaults['type']
  }

  return Client.post(`rentroll`, payload)
    .then((rent: RentRoll) => {
      fetchCalc(rent.propertyid)
      push_undo({
        undofunc: () => deleteOne(propertyid, rent.rentrollid),
        redofunc: () => create(propertyid)
      })
      rentrollMap[rent.rentrollid] = rent
      // merge results unique by rentrollid
      const currentValue = rentrollStream.getValue().value
      rentrollStream.next({
        id,
        state: 'ok',
        value: _.unionBy([rent], currentValue, 'rentrollid')
      })
      rentrollEventStream.next({ type: 'RENT_ROLL_CREATED', payload: rent })
      return rent
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      rentrollStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      fetchRentRoll(propertyid)
      return err
    })
}

function fetchRentRoll(
  propertyId: number,
  includeOptionRecords: boolean = true,
  limit: number = 0,
  offset: number = 0,
  shouldClear: boolean = false
): Promise<RentRoll[] | { message: string }> {
  if (_fetches_.has(propertyId)) return _fetches_.first(propertyId)

  shouldClear && clear();
  rentrollStream.next({ id, state: 'busy', value: rentrollStream.getValue().value })

  let url = `rentroll?propertyid=eq.${propertyId}&status=in.Contractual,Speculative,Mo-To-Mo&order=sortorder`
  if (!includeOptionRecords) url = `rentroll?propertyid=eq.${propertyId}&status=in.Contractual,Speculative,Mo-To-Mo&parentrentrollid=is.null&order=sortorder`
  if (limit && offset >= 0) {
    url = `rentroll?propertyid=eq.${propertyId}&limit=${limit}&offset=${offset}&status=in.Contractual,Speculative,Mo-To-Mo&order=sortorder`
  }

  return _fetches_.watch(propertyId, Client.get(url)
  .then((records: RentRoll[]) => {
    _.forEach(records, (rentrolls) => (rentrollMap[rentrolls.rentrollid] = rentrolls))
    // merge results unique by rentrollid
    rentrollStream.next({ id, state: 'ok', value: records })
    return records
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  }))
}

async function deleteOne(
  propertyid: number,
  rentrollid: number
): Promise<boolean | { message: string }> {
  const currentValue = rentrollStream.getValue().value

  const deleteFn = () => deleteOne(propertyid, rentrollid)
  const refreshFn = () => fetchRentRoll(propertyid)
  if (!(await handleReferences('rentroll', rentrollid, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted rentrollid
  const value = _(currentValue)
    .filter((p) => p.rentrollid !== rentrollid)
    .value()
  value.forEach((p) => (rentrollMap[p.rentrollid] = p))

  rentrollStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(rentrollid).then(() =>
  Client.del(`rentroll?rentrollid=eq.${rentrollid}`)
    .then((rentrolls: RentRoll[]) => {
      fetchCalc(propertyid)
      let value = currentValue

      _.forEach(rentrolls, (rentroll) => {
        delete rentrollMap[rentroll.rentrollid]
        value = value.filter(rr => rr.rentrollid != rentroll.rentrollid)
      })
      rentrollStream.next({ id, state: 'ok', value })

      push_undo({
        undofunc: () => {
          for (let rr of rentrolls){
            Client.post('rentroll', rr)
            rentrollMap[rr.rentrollid] = rr
            value.push(rr)
          }
          rentrollStream.next({ id, state: 'ok', value: [...value] })
        },
        redofunc: () => deleteOne(propertyid, rentrollid)
      })
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      rentrollStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchRentRoll(propertyid)
      return err
    })
  )
}

async function deleteMultiple(
  propertyid: number,
  rentrollids: number[]
): Promise<boolean | { message: string }> {
  const currentValue = rentrollStream.getValue().value

  const deleteFn = (ids) => deleteMultiple(propertyid, ids)
  const refreshFn = () => fetchRentRoll(propertyid)
  if (!(await handleReferences('rentroll', rentrollids, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted rentrollid
  const value = _(currentValue)
    .filter((p) => !rentrollids.includes(p.rentrollid))
    .value()
  value.forEach((p) => (rentrollMap[p.rentrollid] = p))

  rentrollStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(rentrollids).then(() =>
  Client.del(`rentroll?rentrollid=in.${rentrollids}`)
    .then((rentrolls: RentRoll[]) => {
      fetchCalc(propertyid)
      let value = currentValue

      _.forEach(rentrolls, (rentroll) => {
        delete rentrollMap[rentroll.rentrollid]
        value = value.filter(rr => rr.rentrollid != rentroll.rentrollid)
      })
      rentrollStream.next({ id, state: 'ok', value })

      push_undo({
        undofunc: () => {
          for (let rr of rentrolls){
            Client.post('rentroll', rr)
            rentrollMap[rr.rentrollid] = rr
            value.push(rr)
          }
          rentrollStream.next({ id, state: 'ok', value: [...value] })
        },
        redofunc: () => deleteMultiple(propertyid, rentrollids)
      })
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      rentrollStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchRentRoll(propertyid)
      return err
    })
  )
}

function patch(rentroll: RentRoll, _nooptions: boolean = false): Promise<RentRoll | { message: string }> {
  const currentValue = rentrollStream.getValue().value
  const rentrollid = rentroll.rentrollid
  const currentRentroll = _.find(currentValue, { rentrollid })
  const changeset = diff_obj(rentroll, currentRentroll)
  if (_.isEmpty(changeset)) return new Promise(() => currentValue)
  const createsOption = changeset.renewaltype && changeset.renewaltype == 'Option' && currentRentroll.renewaltype != 'Option'
  if (_nooptions) changeset['_nooptions'] = true

  // optimistic update
  rentrollStream.next({
    id,
    state: 'ok',
    value: _.set(_.cloneDeep(currentValue), _.findIndex(currentValue, { rentrollid }), rentroll)
  })

  return _patches_.watch(rentrollid, Client.patch(`rentroll?rentrollid=eq.${rentrollid}`, changeset)
    .then((rentrolls: RentRoll[]) => {
      fetchCalc(rentroll.propertyid)
      rentrollMap[rentrollid] = _.first(rentrolls)
      let newOption
      _.forEach(rentrolls, (rentroll) => {
        const currentValue = rentrollStream.getValue().value
        if (rentroll._deleted) { // remove options deleted by API
          delete rentrollMap[rentroll.rentrollid]
          rentrollStream.next({ id, state: 'ok', value: _.reject(currentValue, {rentrollid: rentroll.rentrollid}) })
        } else if (rentroll.rentrollid != rentrollid) {
          newOption = newOption || rentroll
          rentrollMap[rentroll.rentrollid] = rentroll
          const ix = _.findIndex(currentValue, {rentrollid: rentroll.parentrentrollid}) + 1
          rentrollStream.next({ id, state: 'ok', value: [...currentValue.slice(0,ix), rentroll, ...currentValue.slice(ix)] })
        }
      })

      const addedOptions = changeset.renewaltype == 'Option'
      push_undo({
        undofunc: async () => {
          for (let rr of rentrolls){
            if (rr.rentrollid == rentrollid){
              await patch(_.cloneDeep(currentRentroll), !addedOptions)
              continue
            }
            if (addedOptions) {
              // deleteOne(rr.propertyid, rr.rentrollid)
            } else {
              await Client.post('rentroll', rr)
              rentrollMap[rr.rentrollid] = rr
            }
          }
          fetchRentRoll(rentroll.propertyid)
        },
        redofunc: async () => {
          for (let rr of rentrolls){
            if (rr.rentrollid == rentrollid){
              await patch(rr, addedOptions)
              continue
            }
            if (addedOptions) {
              await Client.post('rentroll', rr)
              rentrollMap[rr.rentrollid] = rr
            } else {
              // deleteOne(rr.propertyid, rr.rentrollid)
            }
          }
          fetchRentRoll(rentroll.propertyid)
        }
      })

      if (createsOption && newOption) {
        rentrollEventStream.next({ type: 'SYNC_OPTIONS', payload: newOption })
      }

      return rentrolls[0]
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      rentrollStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      const propertyid = _.get(currentRentroll, 'propertyid')
      if (propertyid) fetchRentRoll(propertyid)
      return err
    }))
}


function copy(propertyid:number, rentrollid:number) {
  const currentRentRollVal = rentrollStream.getValue().value;
  rentrollStream.next({ id, state: 'busy', value: currentRentRollVal });

  const payload = {table: 'rentroll', recordid: rentrollid, _synchronous: true};
  return Client.post(`msg/svc_recordcopy`, payload)
    .then(async (rentrolls: RentRoll[]) => {
      fetchCalc(propertyid)  
      const newRecord = rentrolls[0]
      push_undo({
        undofunc: () => deleteOne(propertyid, newRecord.rentrollid),
        redofunc: () => copy(propertyid, rentrollid)
      });
      await fetchRentRoll(propertyid, true, 0, 0, false)
      return newRecord
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      rentrollStream.next({ id, state: 'error', error: err, value: currentRentRollVal })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchRentRoll(propertyid)
      return err
    })
}

function copies(propertyid: number, rentrollid: number, count: number) {
  const currentRentRollVal = rentrollStream.getValue().value;
  rentrollStream.next({ id, state: 'busy', value: currentRentRollVal });

  const payload = {table: 'rentroll', recordid: rentrollid, _synchronous: true, count};
  return Client.post(`msg/svc_recordcopy`, payload)
    .then(async (rentrolls: RentRoll[]) => {
      fetchCalc(propertyid)  
      push_undo({
        undofunc: () => deleteMultiple(propertyid, _.map(rentrolls, 'rentrollid')),
        redofunc: () => copies(propertyid, rentrollid, count)
      });
      const fetched = await fetchRentRoll(propertyid, true, 0, 0, false)
      return fetched
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      rentrollStream.next({ id, state: 'error', error: err, value: currentRentRollVal })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchRentRoll(propertyid)
      return err
    })
}

function move(
  rentrollid: number,
  destination: number
): Promise<boolean | { message: string }> {
  if (!_.isFinite(destination)) return Promise.resolve(false)

  const userid = _.get(local('auth.profile'), 'user_id')
  const folderid = _.last(local('folderPath'))
  return folderPermissionService.getFolderUserPermissions(folderid, userid)
    .then((permissions) => {
      if (!(permissions['owner'] || permissions['readwrite'])) {
        notificationService.notify({
          type: "warn",
          message: "Read only users cannot move rent roll records",
          dismiss: 3000
        })
        return {message: 'Read only users cannot move rent roll records'}
      }


      const propertyid = rentrollMap[rentrollid]?.propertyid
      const currentValue = rentrollStream.getValue().value
      const currentIndex = _.findIndex(currentValue, {rentrollid})
      if (currentIndex == destination) return true
      const upward = destination < currentIndex

      const current = _(currentValue).orderBy('sortorder').map('rentrollid').value()
      let groups = function() {
        let visited = {}
        let groups: number[][] = []
        for (let id of current) {
          if (id in visited) continue
          const descendents = findDescendents(id)
          groups.push([id, ...descendents])
          _.forEach([id, ...descendents], id => visited[id] = true)
        }
        return groups
        function findDescendents(rentrollid):number[] {
          const children = _(currentValue).filter({parentrentrollid: rentrollid}).map('rentrollid').value()
          return _.filter(_.flatMapDeep(children, c => [c, findDescendents(c)]))
        }
      }()
      let currentGroup = _.find(groups, g => g.includes(rentrollid))
      groups = _.reject(groups, g => _.isEqual(g, currentGroup))
      let orders: number[] = []
      while (orders.length < current.length){
        const next_length = orders.length + (groups[0]||[]).length
        if (upward && next_length > destination) {
          if (currentGroup) {
            orders.push(...currentGroup)
            currentGroup = null
          }
        }
        if (!upward && (orders.length >= destination || !groups.length)) {
          if (currentGroup) {
            orders.push(...currentGroup)
            currentGroup = null
          }
        }
        const x = groups.shift()
        if (x) orders.push(...x)
      }

      const undo = {
        undofunc: () => move(rentrollid, _.findIndex(current, rentrollid) + 1),
        redofunc: () => move(rentrollid, destination)
      }
      const value = _.map(orders, (id, ix) => {
        rentrollMap[id] = { ...rentrollMap[id], sortorder: ix + 1 }
        return rentrollMap[id]
      })
      rentrollStream.next({ id, state: 'ok', value })

      const payload = {
        table: 'rentroll',
        organizationid: currentValue['organizationid'],
        orders,
        propertyid,
        _synchronous: true
      }

      // update the db
      return Client.post(`msg/svc_reorder`, payload)
        .then(() => {
          // update already happened optimistically
          push_undo(undo)
          fetchCalc(propertyid)
          return true
        })
        .catch((err: { error: {text: string }}) => {
          // on error overwrite optimistic update
          rentrollStream.next({ id, state: 'error', error: err, value: currentValue })
          notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
          const currentPropertyId = local('property');
          fetchRentRoll(currentPropertyId)
          return {message: err.error.text}
        })

    })
    .catch((err) => {
      return {message: err?.error?.text}
    })
}

function sort(payload:{organizationId:number, propertyId:number, sortBy:string, sortOrder:string}){
  const userid = _.get(local('auth.profile'), 'user_id')
  const folderid = _.last(local('folderPath'))
  folderPermissionService.getFolderUserPermissions(folderid, userid)
    .then((permissions) => {
      if(permissions['owner'] || permissions['readwrite']) {
        Client.post(`msg/svc_rentroll_reorder`, payload)
        notificationService.notify({
          type: "info",
          message: "Rent roll sort initiated.",
          dismiss: 3000
        })
      } else {
        notificationService.notify({
          type: "warn",
          message: "Read only users cannot sort the rent roll",
          dismiss: 3000
        })
      }
    })
    .catch((err) => {
      return {message: err.error.text}
    })
}

// public module interface //

const rentrollService = {
  stream: () => rentrollStream.asObservable(),
  value: () => rentrollStream.getValue().value,
  eventStream: () => rentrollEventStream.asObservable(),
  clear,
  copy,
  copies,
  create,
  deleteOne,
  deleteMultiple,
  get,
  fetchRentRoll,
  move,
  patch,
  sort,
  refresh
}

export default rentrollService
